package com.eladfinish.bible.contest.platform

/** The longest label the server's device_model column holds. */
private const val MAX_LABEL_LENGTH = 50

/**
 * Turns a browser user agent into a short label such as "Chrome 154 / macOS", for the
 * device_model field of the search analytics events. The raw user agent is about 120 characters
 * and the column holds 50, so the cut-off string told neither the browser nor its version.
 *
 * Order matters: Edge, Opera and Samsung Internet also say "Chrome/", every Chromium browser also
 * says "Safari/", and iOS browsers name themselves with their own token (CriOS, FxiOS, EdgiOS).
 */
internal fun userAgentLabel(userAgent: String): String {
    val browser = browserOf(userAgent)
    val os = osOf(userAgent)
    return "$browser / $os".take(MAX_LABEL_LENGTH)
}

private val browserTokens = listOf(
    "EdgiOS" to "Edge",
    "EdgA" to "Edge",
    "Edg" to "Edge",
    "OPR" to "Opera",
    "SamsungBrowser" to "Samsung Internet",
    "CriOS" to "Chrome",
    "FxiOS" to "Firefox",
    "Firefox" to "Firefox",
    "Chrome" to "Chrome",
)

private fun browserOf(userAgent: String): String {
    for ((token, name) in browserTokens) {
        val major = majorVersionAfter(userAgent, "$token/") ?: continue
        return "$name $major"
    }
    if (userAgent.contains("Safari/")) {
        val major = majorVersionAfter(userAgent, "Version/")
        return if (major != null) "Safari $major" else "Safari"
    }
    return "Other"
}

private fun majorVersionAfter(userAgent: String, token: String): String? {
    val start = userAgent.indexOf(token)
    if (start < 0) return null
    val major = userAgent.substring(start + token.length).takeWhile { it.isDigit() }
    return major.ifEmpty { null }
}

private fun osOf(userAgent: String): String = when {
    userAgent.contains("iPhone") || userAgent.contains("iPod") -> "iOS"
    userAgent.contains("iPad") -> "iPadOS"
    userAgent.contains("Android") -> "Android"
    userAgent.contains("Windows") -> "Windows"
    userAgent.contains("CrOS") -> "ChromeOS"
    userAgent.contains("Macintosh") || userAgent.contains("Mac OS X") -> "macOS"
    userAgent.contains("Linux") -> "Linux"
    else -> "Other"
}
